import React, { useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Typography, Chip, Tooltip, Link } from "@material-ui/core";
import {
  Speed,
  Tune,
  RecordVoiceOver,
  CategoryOutlined,
  LocalLibrary,
  School,
  InfoOutlined,
  Assignment,
  AccountBalance,
  Description,
} from "@material-ui/icons";

import FileDownload from "./FileDownload";
import JobActionsCell from "./JobActionsCell";
import { Section, MetaGrid, MetaRow } from "./JobDetailsDrawer";
import { useTranslation } from "../hooks/useTranslation";

const useStyles = makeStyles((theme) => ({
  modeChip: {
    height: 22,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.3px",
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  modeStandard: {
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    color: "var(--ikaro-text-primary)",
    border: "1px solid var(--ikaro-border-default)",
  },
  modePremium: {
    backgroundColor: "var(--ikaro-orange-light)",
    color: "var(--ikaro-orange-primary)",
    border: "1px solid var(--ikaro-orange-primary)",
  },
  modeNext: {
    backgroundColor: "var(--ikaro-purple-light)",
    color: "var(--ikaro-purple-accent)",
    border: "1px solid var(--ikaro-purple-accent)",
    '[data-theme="dark"] &': {
      color: "#c4a4e0",
      borderColor: "#c4a4e0",
    },
  },
  paramValueRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  paramLevel: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.6),
  },
  /* Indicador visual do nível selecionado na escala 1-3. Três pontos
     compactos onde os N primeiros ficam preenchidos com a cor de marca.
     Substitui o número solto, que não comunicava nada por si só. */
  paramScale: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
  },
  paramScaleDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "var(--ikaro-border-default)",
  },
  paramScaleDotActive: {
    backgroundColor: "var(--ikaro-orange-primary)",
  },
  paramLabel: {
    fontSize: "0.82rem",
    color: "var(--ikaro-text-primary)",
    fontWeight: 500,
  },
  overrideBadge: {
    height: 18,
    fontSize: "0.62rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    backgroundColor: "var(--ikaro-purple-light)",
    color: "var(--ikaro-purple-accent)",
    border: "1px solid var(--ikaro-purple-accent)",
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(0.6),
      paddingRight: theme.spacing(0.6),
    },
  },
  helpIcon: {
    fontSize: "0.95rem",
    color: "var(--ikaro-text-muted)",
    cursor: "help",
  },
  inlineNote: {
    fontSize: "0.72rem",
    color: "var(--ikaro-text-muted)",
    fontStyle: "italic",
    marginTop: theme.spacing(0.5),
  },
  teseRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25),
    padding: theme.spacing(0.75, 1),
    border: "1px solid var(--ikaro-border-light)",
    borderRadius: theme.spacing(0.5),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    marginBottom: theme.spacing(0.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  teseNup: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "var(--ikaro-text-primary)",
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  teseFileWrap: {
    paddingLeft: theme.spacing(0.25),
    "& .MuiSvgIcon-root": {
      color: "var(--ikaro-purple-accent)",
    },
    "& span[class*=fileName]": {
      color: "var(--ikaro-purple-accent)",
    },
  },
  emptyState: {
    fontSize: "0.78rem",
    color: "var(--ikaro-text-muted)",
    fontStyle: "italic",
  },
  rulesBlock: {
    marginBottom: theme.spacing(1.25),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  rulesHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  rulesTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "var(--ikaro-text-primary)",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "& .MuiSvgIcon-root": {
      fontSize: "0.95rem",
      color: "var(--ikaro-text-muted)",
    },
  },
  rulesText: {
    fontSize: "0.8rem",
    lineHeight: 1.45,
    color: "var(--ikaro-text-primary)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    border: "1px solid var(--ikaro-border-light)",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
  },
  /* Clipping por número de linhas para regras grandes. line-clamp tem
     suporte amplo (Safari, Chrome, Firefox 68+) e respeita o lineHeight do
     bloco — fica visualmente igual ao texto, só com fade implícito do
     truncamento. */
  rulesTextClipped: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 6,
    overflow: "hidden",
  },
  rulesToggle: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-block",
    color: "var(--ikaro-orange-primary)",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  actionsWrap: {
    /* Wrapper para o conjunto de ações no rodapé do drawer. Mantém a mesma
       hierarquia visual do JobActionsCell (primário > secundário > menu) mas
       permite alinhamento horizontal contido na largura do drawer. */
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0),
  },
}));

const PARAM_DEFINITIONS = [
  {
    key: "creativity",
    i18nKey: "creativity",
    icon: Tune,
  },
  {
    key: "tone",
    i18nKey: "tone",
    icon: RecordVoiceOver,
  },
  {
    key: "scope_topics",
    i18nKey: "scopeTopics",
    icon: CategoryOutlined,
  },
  {
    key: "complexity",
    i18nKey: "complexity",
    icon: LocalLibrary,
  },
  {
    key: "foundation_depth",
    i18nKey: "foundationDepth",
    icon: School,
  },
];

/* Lê o modo de geração consolidando as duas fontes possíveis: o
   `input_payload.modo_geracao` (sempre presente em jobs novos) e o
   `output_params.modo_geracao` (preenchido pelo worker ao concluir). */
const resolveModoGeracao = (job) => {
  return (
    job?.input_payload?.modo_geracao ||
    job?.output_params?.modo_geracao ||
    null
  );
};

const isRestrictedToTheses = (customProfile) => {
  if (!customProfile) return false;
  return customProfile.creativity === 1 && customProfile.scope_topics === 1;
};

/* Bloco "Configuração da Geração": modo (Next/Standard/Premium) + 5 parâmetros
   de personalização. Quando o modo é restritivo (criatividade=1 +
   abrangência=1), os demais parâmetros não influenciam o resultado, então
   são ocultados para evitar ruído visual. Parâmetros sobrescritos pelo
   usuário ganham um chip discreto "sobrescrito"; valores padrão da célula
   ficam sem marcação. */
export const ContestacaoConfigSection = ({ job }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const modo = resolveModoGeracao(job);
  const customProfile = job?.input_payload?.custom_profile || {};
  const overrides = new Set(
    Array.isArray(job?.input_payload?.custom_profile_overrides)
      ? job.input_payload.custom_profile_overrides
      : [],
  );
  const restricted = isRestrictedToTheses(customProfile);
  const visibleParams = restricted
    ? PARAM_DEFINITIONS.filter(
        (p) => p.key === "creativity" || p.key === "scope_topics",
      )
    : PARAM_DEFINITIONS;

  const hasAnyParam = visibleParams.some(
    (p) => customProfile[p.key] != null,
  );

  if (!modo && !hasAnyParam) return null;

  return (
    <Section
      title={t("contestacao.drawer.sections.generation")}
      accent="primary"
    >
      <MetaGrid>
        {modo && (
          <MetaRow
            icon={<Speed />}
            label={t("contestacao.drawer.fields.mode")}
            value={
              <Chip
                size="small"
                label={t(
                  `contestacao.drawer.modeLabels.${modo}`,
                  modo,
                )}
                className={`${classes.modeChip} ${
                  modo === "premium"
                    ? classes.modePremium
                    : modo === "next"
                      ? classes.modeNext
                      : classes.modeStandard
                }`}
              />
            }
            copyValue={null}
          />
        )}
        {visibleParams.map((param) => {
          const level = customProfile[param.key];
          if (level == null) return null;
          const IconComp = param.icon;
          /* Reaproveitamos a mesma origem i18n usada pela tela de
             "Configurações da célula" (CellParamsModal/Contestacao.js):
             contestacaoCreate.customization.{param}.{title|levels|tooltips}.
             Isso garante que o vocabulário visto pelo advogado é o mesmo
             em todos os pontos do produto. */
          const i18nBase = `contestacaoCreate.customization.${param.i18nKey}`;
          const levelLabel = t(`${i18nBase}.levels.${level}`, "");
          const tooltip = t(`${i18nBase}.tooltips.${level}`, "");
          const title = t(`${i18nBase}.title`, param.key);
          const isOverridden = overrides.has(param.key);
          return (
            <MetaRow
              key={param.key}
              icon={<IconComp />}
              label={title}
              value={
                <Box component="span" className={classes.paramValueRow}>
                  <Box component="span" className={classes.paramLevel}>
                    <Box component="span" className={classes.paramScale}>
                      {[1, 2, 3].map((n) => (
                        <span
                          key={n}
                          className={`${classes.paramScaleDot} ${
                            n <= level ? classes.paramScaleDotActive : ""
                          }`}
                        />
                      ))}
                    </Box>
                    <span className={classes.paramLabel}>{levelLabel}</span>
                  </Box>
                  {tooltip && (
                    <Tooltip title={tooltip} arrow placement="top">
                      <InfoOutlined className={classes.helpIcon} />
                    </Tooltip>
                  )}
                  {isOverridden && (
                    <Chip
                      size="small"
                      label={t("contestacao.drawer.overrideBadge")}
                      className={classes.overrideBadge}
                    />
                  )}
                </Box>
              }
              copyValue={null}
            />
          );
        })}
      </MetaGrid>
      {restricted && (
        <Typography className={classes.inlineNote}>
          {t("contestacao.drawer.restrictedNote")}
        </Typography>
      )}
    </Section>
  );
};

/* Bloco "Teses Utilizadas": lista as teses (PDFs) que foram selecionadas
   pela IA durante o passo 3 da geração. Cada item mostra o NUP do processo
   e um FileDownload com preview/download do PDF original. */
export const ContestacaoTesesSection = ({ job }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const teses = job?.output_params?.teses_utilizadas;
  if (!Array.isArray(teses) || teses.length === 0) return null;

  return (
    <Section
      title={t("contestacao.drawer.sections.teses")}
      accent="secondary"
    >
      <Box>
        {teses.map((tese, idx) => (
          <Box
            key={`tese-${tese.numero_processo || idx}-${idx}`}
            className={classes.teseRow}
          >
            <Typography component="span" className={classes.teseNup}>
              {tese.numero_processo || t("contestacao.drawer.teseNupUnknown")}
            </Typography>
            <Box className={classes.teseFileWrap}>
              <FileDownload
                file={{
                  filename: tese.filename,
                  token: tese.token,
                }}
                previewType="pdf"
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
};

/* Texto de regras com clamp por linhas + toggle "Ver mais"/"Ver menos".
   Detecta automaticamente se o conteúdo extrapola o número de linhas
   visíveis (via scrollHeight vs clientHeight no estado clipado) e só
   renderiza o toggle quando o clip realmente esconde algo. Default 6
   linhas. */
const CollapsibleRulesText = ({ text, maxLines = 6 }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Mede no estado clipado (independente do toggle atual) instalando um
    // probe temporário com a classe de clip; medir o nó real quando ele já
    // está em modo "expandido" daria falso negativo.
    const clone = el.cloneNode(true);
    clone.className = `${classes.rulesText} ${classes.rulesTextClipped}`;
    clone.style.position = "absolute";
    clone.style.visibility = "hidden";
    clone.style.pointerEvents = "none";
    clone.style.width = `${el.clientWidth}px`;
    el.parentNode.appendChild(clone);
    setOverflows(clone.scrollHeight > clone.clientHeight + 1);
    el.parentNode.removeChild(clone);
  }, [text, classes, maxLines]);

  const clipped = !expanded && overflows;

  return (
    <Box>
      <Typography
        component="div"
        ref={ref}
        className={`${classes.rulesText} ${
          clipped ? classes.rulesTextClipped : ""
        }`}
        style={clipped ? { WebkitLineClamp: maxLines } : undefined}
      >
        {text}
      </Typography>
      {overflows && (
        <Link
          component="button"
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={classes.rulesToggle}
        >
          {expanded
            ? t("contestacao.drawer.fields.showLess")
            : t("contestacao.drawer.fields.showMore")}
        </Link>
      )}
    </Box>
  );
};

/* Bloco "Instruções Aplicadas": mostra as instruções específicas deste
   processo (instrucoes_especificas), as instruções para criação da
   contestação (regras) e as instruções para análise de subsídios
   (regras_subsidios). As regras internas NUNCA são exibidas aqui — são
   para uso interno da equipe. As duas últimas recebem um chip "sobrescrito"
   quando o usuário substituiu o padrão da célula nesta execução. */
export const ContestacaoRulesSection = ({ job }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const instrucoes = (job?.input_payload?.instrucoes_especificas || "").trim();
  const regras = (job?.input_payload?.regras || "").trim();
  const regrasSubsidios = (job?.input_payload?.regras_subsidios || "").trim();
  const regrasOverridden = !!job?.input_payload?.regras_overridden;
  const regrasSubsidiosOverridden =
    !!job?.input_payload?.regras_subsidios_overridden;

  if (!instrucoes && !regras && !regrasSubsidios) return null;

  return (
    <Section
      title={t("contestacao.drawer.sections.rules")}
      accent="neutral"
    >
      {regrasSubsidios && (
        <Box className={classes.rulesBlock}>
          <Box className={classes.rulesHeader}>
            <Typography component="span" className={classes.rulesTitle}>
              <Description />
              {t("contestacao.drawer.fields.rulesSubsidios")}
            </Typography>
            {regrasSubsidiosOverridden && (
              <Chip
                size="small"
                label={t("contestacao.drawer.overrideBadge")}
                className={classes.overrideBadge}
              />
            )}
          </Box>
          <CollapsibleRulesText text={regrasSubsidios} />
        </Box>
      )}
      {instrucoes && (
        <Box className={classes.rulesBlock}>
          <Box className={classes.rulesHeader}>
            <Typography component="span" className={classes.rulesTitle}>
              <Assignment />
              {t("contestacao.drawer.fields.rulesPontuais")}
            </Typography>
          </Box>
          <CollapsibleRulesText text={instrucoes} />
        </Box>
      )}
      {regras && (
        <Box className={classes.rulesBlock}>
          <Box className={classes.rulesHeader}>
            <Typography component="span" className={classes.rulesTitle}>
              <AccountBalance />
              {t("contestacao.drawer.fields.rulesCliente")}
            </Typography>
            {regrasOverridden && (
              <Chip
                size="small"
                label={t("contestacao.drawer.overrideBadge")}
                className={classes.overrideBadge}
              />
            )}
          </Box>
          <CollapsibleRulesText text={regras} />
        </Box>
      )}
    </Section>
  );
};

/* Bloco "Ações": replica no rodapé do drawer as mesmas ações que apareceriam
   na coluna de ações da linha da tabela, respeitando as mesmas regras de
   permissão e visibilidade (`hidden(job)`). Reusa `JobActionsCell` para
   manter a hierarquia visual consistente (primário > secundário > menu). */
export const ContestacaoActionsSection = ({ job, actions, onAction }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  if (!Array.isArray(actions) || actions.length === 0) return null;

  const hasVisible = actions.some((a) => !a.hidden || !a.hidden(job));
  if (!hasVisible) return null;

  return (
    <Section
      title={t("contestacao.drawer.sections.actions")}
      accent="primary"
    >
      <Box className={classes.actionsWrap}>
        <JobActionsCell job={job} actions={actions} onAction={onAction} />
      </Box>
    </Section>
  );
};
